import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/delay';

import swal from "bootstrap-sweetalert";
import * as moment from "moment";
import * as _ from 'lodash';

import { Subscription } from "rxjs/Subscription";
import { ActivatedRoute, Router } from "@angular/router";
import { BsLocaleService, BsDropdownDirective } from "ngx-bootstrap";
import { PlannedService } from "app/models/plannedService";
import { PlannedServicesDoneFilterOptions } from "app/dto/plannedServicesDoneFilterOptions";
import { PlannedServiceService } from "app/services/plannedService.service";
import { ScrollToAnchorService } from "app/services/scroll-to-anchor.service";
import { PagedResult } from "app/models/PagedResult";

@Component({
  templateUrl: './plannedService-list.component.html',
  styleUrls: ['./plannedService-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PlannedServiceListComponent implements OnInit, OnDestroy {
  @ViewChild('dropdown')
  private searchDropdown: BsDropdownDirective;

  constructor(
    private _plannedServiceService: PlannedServiceService,
    private _changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _localeService: BsLocaleService,
    private _scrollToAnchorService: ScrollToAnchorService
  ) { }

  private plannedServices: PlannedService[];
  private plannedServicesObservable: Observable<PagedResult<PlannedService>>;
  private plannedServicesSubscription: Subscription;

  private pagination = {
    id: 'plannedServices_pagination',
    itemsPerPage: 20,
    currentPage: 1,
    totalItems: 0
  }
  private sortBy = "Name";
  private isDesc = false;
  private _errorState: PlannedServiceListComponent.ErrorState = PlannedServiceListComponent.ErrorState.None;


  ngOnInit() {
    this._localeService.use('ru');
    this.loadPageWithUrlFilterParams();
  }

  ngOnDestroy(): void {
    this.clearSubscription();
    swal.close();
  }

  private setItemsPerPage(itemsPerPage: number): void {
    this.setQueryParams({ count: itemsPerPage });
  }

  private setPage(page: number) {
    this.setQueryParams({ page: page });
  }

  private sort(sortBy: string, isDesc: boolean) {
    this.setQueryParams(
      {
        sortBy: sortBy,
        isDesc: isDesc
      });
  }

  private loadPageWithUrlFilterParams() {
    this.activatedRoute.queryParams.subscribe(params => {

      if (params['deadlineStart'] && params['deadlineEnd'])
        this._filterOptions.deadlineRange = [new Date(params['deadlineStart']), new Date(params['deadlineEnd'])];
      else
        this._filterOptions.deadlineRange = [];

      this._filterOptions.location = params['location'] ? params['location'] : null;
      this._filterOptions.name = params['name'] ? params['name'] : null;
      this.isDesc = params['isDesc'] ? JSON.parse(params['isDesc']) : this.isDesc;
      this.sortBy = params['sortBy'] ? params['sortBy'] : this.sortBy;
      this.pagination.itemsPerPage = params['count'] ? params['count'] : this.pagination.itemsPerPage;
      this.pagination.currentPage = params['page'] ? params['page'] : 1;

      this.setSearchString();
      this.clearSubscription();

      this.loadData();
    });
  }

  loadData() {
    this._errorState = PlannedServiceListComponent.ErrorState.None;
    // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
    this._changeDetector.markForCheck();

    this.clearSubscription();

    let newPlannedServiceObservable = this._plannedServiceService
      .getPlanedServicesPerPage(this.sortBy,
        this.isDesc,
        this.pagination.itemsPerPage,
        (this.pagination.currentPage - 1) * this.pagination.itemsPerPage,
        this._filterOptions);

    this.plannedServicesObservable = newPlannedServiceObservable;

    this.plannedServicesSubscription = this.plannedServicesObservable
      .subscribe(res => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (newPlannedServiceObservable === this.plannedServicesObservable) {
          this.pagination.totalItems = res.totalCount;
          this.plannedServices = res.items;
          this._changeDetector.markForCheck();
          this.restoreScrollPosition();
        }
      }, error => {
        if (error.status == 403 || error.status == 404) {
          this.router.navigateByUrl('/pagenotfound');
        } else {
          // если на форме сейчас те элементы, которыми управляет данный Observable
          if (newPlannedServiceObservable === this.plannedServicesObservable) {
            this._errorState = PlannedServiceListComponent.ErrorState.UnknownError;
            // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
            this._changeDetector.markForCheck();

            swal({
              title: 'Ошибка',
              text: 'Не удалось получить данные объектов обслуживания. Попробуйте снова',
              type: 'error',
              confirmButtonText: 'Попробовать еще раз'
            }, () => {
              this.loadData();
            });
          }
        }
      });
  }

  private clearSubscription() {
    if (this.plannedServicesSubscription)
      this.plannedServicesSubscription.unsubscribe();

    this.plannedServicesSubscription = null;
    this.plannedServicesObservable = null;
    this.plannedServices = null;
  }

  private setSearchString() {
    let searchStrings: string[] = new Array();

    if (this._filterOptions) {
      if (this._filterOptions.location)
        searchStrings.push("Локация:(" + this._filterOptions.location + ")");

      if (this._filterOptions.name)
        searchStrings.push("Название:(" + this._filterOptions.name + ")");

      if (this._filterOptions.deadlineRange) {
        if (this._filterOptions.deadlineRange.length >= 1 && this._filterOptions.deadlineRange[0])
          searchStrings.push("От:(" + moment(this._filterOptions.deadlineRange[0]).format("DD.MM.YYYY") + ")");

        if (this._filterOptions.deadlineRange.length == 2 && this._filterOptions.deadlineRange[1])
          searchStrings.push("До:(" + moment(this._filterOptions.deadlineRange[1]).format("DD.MM.YYYY") + ")");
      }
    }
    this._searchString = _.join(searchStrings, ' ');
  }

  private goToDetailPlannedService(elementId : string, plannedServiceId : number) {
    let currentUrl = this.router.url.split('#')[0];
    this.router.navigate([`/plannedService/${plannedServiceId}`]);
    window.history.replaceState({}, '', currentUrl + "#" + elementId);
  }

  private restoreScrollPosition() : void {
    const fragment = this.activatedRoute.snapshot.fragment;

    if (fragment) {
      let index = parseInt(fragment.split('_')[1]);
      if (index < this.plannedServices.length) {
        this._scrollToAnchorService.scroll(fragment);
      } else {
        this._scrollToAnchorService.scroll("row_" + (this.plannedServices.length - 1));
      }
    }
  }

  private setQueryParams(queryParams: object) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        ...queryParams
      }
    });
    this.searchDropdown.hide();
  }

  private setFilterOptions() {
    this.setQueryParams(
      {
        location: this._filterOptions.location,
        name: this._filterOptions.name,
        deadlineStart: this.formatDate(this._filterOptions.deadlineRange, 0),
        deadlineEnd: this.formatDate(this._filterOptions.deadlineRange, 1)
      });
  }

  private formatDate(dateRange: Date[], index: number) {
    if (dateRange == null || dateRange[index] == null)
      return null;

    return moment(dateRange[index]).toISOString();
  }

  private resetFilterOptions(): void {

    //Сброс фильтров
    this._filterOptions.location = null;
    this._filterOptions.name = null;
    this._filterOptions.deadlineRange = null;

    this.setFilterOptions();
  }

  private convertPlannedServiceStatusToText(status: PlannedService.Status): string {
    switch (status) {
      case PlannedService.Status.Planned:
        return "Запланировано";
      case PlannedService.Status.Done:
        return "Выполнено";
      case PlannedService.Status.Canceled:
        return "Отменено";
      default:
        return "";
    }
  }

  private getColorStatus(status: PlannedService.Status): string {
    switch (status) {
      case PlannedService.Status.Planned: return "label-warning";
      case PlannedService.Status.Done: return "label-success";
      case PlannedService.Status.Canceled: return "label-primary";
      default: return "label-default";
    }
  }

  private _searchString: string;
  private isTableRowCollapsed: boolean[] = [];
  private _filterOptions: PlannedServicesDoneFilterOptions = new PlannedServicesDoneFilterOptions();
  private ErrorStateEnum = PlannedServiceListComponent.ErrorState;
}

export namespace PlannedServiceListComponent {

  export enum ErrorState {
    /**
     * Нет ошибок
     */
    None,
    /**
     * Другая ошибка при получении данных с сервера
     */
    UnknownError
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/client/plannedService/list/plannedService-list.component.ts